'use client';

import {
  ErrorNotice,
  ErrorNoticeAction,
  ErrorNoticeActionVariant,
  ErrorNoticeActions,
  ErrorNoticeCard,
  ErrorNoticeCode,
  ErrorNoticeCodeValue,
  ErrorNoticeMark,
  ErrorNoticeMessage,
  ErrorNoticeTitle,
  ErrorNoticeVariant,
} from '@vs-shop/ui/errorNotice';
import * as Sentry from '@sentry/nextjs';
import { Bug } from 'lucide-react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { navigateFullPage } from '../../../utils/router';

type Props = {
  error: Error & { digest?: string };
  fullPage?: boolean;
  showActions?: boolean;
};

/**
 * rcprofi's failure page, composed on `@vs-shop/ui/errorNotice`.
 *
 * What stays here is what the section deliberately does not know: the capture
 * to Sentry, the copy, the drawing, and what "back to the shop" means. The
 * `intl` this reads is the provider `host/GlobalErrorPage.tsx` mounts around it
 * — the section itself needs none, which is what lets it render under a
 * global-error boundary where the root layout is gone.
 */
export function GlobalErrorContent({ error, fullPage = false, showActions = true }: Props) {
  const intl = useIntl();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <ErrorNotice
      role="alert"
      aria-live="assertive"
      data-slot="global-error"
      variant={fullPage ? ErrorNoticeVariant.PAGE : ErrorNoticeVariant.INLINE}
    >
      <ErrorNoticeCard data-slot="global-error-content">
        <ErrorNoticeMark>
          <Bug className="text-destructive" />
        </ErrorNoticeMark>

        <ErrorNoticeTitle render={<h1 />}>
          {intl.formatMessage({
            id: 'component.client.error.globalErrorContent.title',
            defaultMessage: 'Něco se pokazilo',
          })}
        </ErrorNoticeTitle>

        <ErrorNoticeMessage>
          {intl.formatMessage({
            id: 'component.client.error.globalErrorContent.message',
            defaultMessage:
              'Omlouváme se, ale došlo k neočekávané chybě. Náš tým byl o problému informován a pracujeme na jeho vyřešení.',
          })}
        </ErrorNoticeMessage>

        {showActions && (
          <ErrorNoticeActions>
            <ErrorNoticeAction
              variant={ErrorNoticeActionVariant.PRIMARY}
              data-slot="global-error-retry"
              onClick={() => {
                window.location.reload();
              }}
            >
              {intl.formatMessage({
                id: 'component.client.error.globalErrorContent.button.retry',
                defaultMessage: 'Zkusit znovu',
              })}
            </ErrorNoticeAction>

            <ErrorNoticeAction
              variant={ErrorNoticeActionVariant.SECONDARY}
              data-slot="global-error-home"
              onClick={() => {
                navigateFullPage('/');
              }}
            >
              {intl.formatMessage({
                id: 'component.client.error.globalErrorContent.button.home',
                defaultMessage: 'Zpět na domovskou stránku',
              })}
            </ErrorNoticeAction>
          </ErrorNoticeActions>
        )}

        {error.digest && (
          <ErrorNoticeCode>
            {intl.formatMessage({
              id: 'component.client.error.globalErrorContent.errorCode',
              defaultMessage: 'Kód chyby:',
            })}{' '}
            <ErrorNoticeCodeValue>{error.digest}</ErrorNoticeCodeValue>
          </ErrorNoticeCode>
        )}
      </ErrorNoticeCard>
    </ErrorNotice>
  );
}
